import {
  useParams,
  useNavigate } from "react-router-dom";
import { useState,
  useEffect } from "react";
import {
  CategorySidebar,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Card,
  CardContent
} from "@b2b/ui-components";

import {
  Search,
  Grid3X3,
  List,
  SlidersHorizontal,
  TrendingUp,
  ChevronRight,
} from "lucide-react";
import { adminService } from "@b2b/services";
import { useAuth } from "../../auth/Authprovider";
import { useChatUnreadHeader } from "../../chat/ChatUnreadHeaderProvider";

// Placeholder image as data URI (no external request needed)
const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect fill='%23e5e7eb' width='400' height='300'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='18' fill='%239ca3af'%3ENo Image%3C/text%3E%3C/svg%3E";

const CategoryDetail = () => {
  const { categorySlug } = useParams();
  const { logout } = useAuth();
  const chatUnread = useChatUnreadHeader();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<any>(null);
  const [subcategories, setSubcategories] = useState<any[]>([]);
  const [topCategories, setTopCategories] = useState<any[]>([]);
  const [selectedLevel0Id, setSelectedLevel0Id] = useState<string | null>(null);
  const [selectedLevel1Id, setSelectedLevel1Id] = useState<string | null>(null);
  const [currentCategoryId, setCurrentCategoryId] = useState<string | null>(
    null
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("relevance");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    adminService
      .getCategories(true)
      .then((data) => {
        if (!active) return;
        const all = Array.isArray(data) ? data : [];
        const tops = all.filter((c: any) => !c.parentId);
        // Filter to only show categories with products
        const categoriesWithProducts = tops.filter(
          (c: any) => (c._count?.products || c.productCount || 0) > 0
        );
        setTopCategories(categoriesWithProducts);
        const found = all.find(
          (cat: any) =>
            cat.slug === categorySlug || String(cat.id) === categorySlug
        );
        setCategory(found || null);
        if (found) {
          setSelectedLevel0Id(found.slug || String(found.id ?? ""));
          setSelectedLevel1Id(null);
          setCurrentCategoryId(found.id);
          // Check if category has children
          if (found.children && found.children.length > 0) {
            setSubcategories(found.children);
          } else {
            // No children, navigate to product list page for this category
            navigate(`/products/category/${found.id}`);
          }
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        setError("Failed to load category");
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [categorySlug]);

  const filteredSubcategories = subcategories.filter(
    (subcategory) =>
      searchQuery === "" ||
      subcategory.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (subcategory.description || "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
  );

  const getCategoryTitle = (slug: string) => category?.name || "Category";

  const handleSubcategoryClick = (sub: any) => {
    // If subcategory has children (level 3), show them
    // Otherwise, navigate to product list page for this category
    if (sub.children && sub.children.length > 0) {
      // Update to show level 3 subcategories
      setSubcategories(sub.children || []);
      setSelectedLevel1Id(sub.slug || String(sub.id ?? ""));
    } else {
      // No children, navigate to product list page
      navigate(`/products/category/${sub.id}`);
    }
  };

  // Breadcrumb path (Level 0 -> Level 1)
  const level0Node = selectedLevel0Id
    ? topCategories.find(
        (c) => (c.slug || String(c.id ?? "")) === selectedLevel0Id
      )
    : null;
  const level1Node =
    level0Node && selectedLevel1Id
      ? (level0Node.children || []).find(
          (s: any) => (s.slug || String(s.id ?? "")) === selectedLevel1Id
        )
      : null;

  return (
    <div
      className="min-h-screen flex flex-col bg-[#ffffff] [background-image:radial-gradient(ellipse_100%_90%_at_0%_28%,rgb(255_240_229/0.92)_0%,transparent_58%),radial-gradient(ellipse_95%_85%_at_100%_18%,rgb(240_247_249/0.9)_0%,transparent_55%)]"
    >
      {/* Header */}
      <BizcoverIndiaFigmaHeader />

      {/* Main Section */}
      <div className="flex flex-1 relative min-h-[calc(100vh-4rem)]">
        {/* Sidebar for large screens */}
        <div className="hidden lg:block w-80 border-r border-gray-200">
          <CategorySidebar
            className="h-full overflow-y-auto"
            categories={topCategories}
            selectedLevel0Id={selectedLevel0Id}
            selectedLevel1Id={selectedLevel1Id}
            onSelectLevel0={(id) => {
              // Handle deselection (empty string or same selection)
              if (!id || id === "" || selectedLevel0Id === id) {
                setSelectedLevel0Id(null);
                setSelectedLevel1Id(null);
                setSubcategories([]);
                setShowProducts(false);
                setProducts([]);
                return;
              }
              setSelectedLevel0Id(id);
              setSelectedLevel1Id(null);
              const level0 = topCategories.find(
                (c) => (c.slug || String(c.id ?? "")) === id
              );
              if (level0) {
                setCurrentCategoryId(level0.id);
                if (level0.children && level0.children.length > 0) {
                  setSubcategories(level0.children);
                } else {
                  // No children, navigate to product list page
                  navigate(`/products/category/${level0.id}`);
                }
              }
            }}
            onSelectLevel1={(id) => {
              // Toggle selection for Level 1
              if (selectedLevel1Id === id) {
                setSelectedLevel1Id(null);
                // Show back Level 1 list for the selected Level 0
                const level0 = selectedLevel0Id
                  ? topCategories.find(
                      (c) => (c.slug || String(c.id ?? "")) === selectedLevel0Id
                    )
                  : null;
                if (level0) {
                  if (level0.children && level0.children.length > 0) {
                    setSubcategories(level0.children);
                  } else {
                    // No children, navigate to product list page
                    navigate(`/products/category/${level0.id}`);
                  }
                }
                return;
              }
              setSelectedLevel1Id(id);
              const level0 = selectedLevel0Id
                ? topCategories.find(
                    (c) => (c.slug || String(c.id ?? "")) === selectedLevel0Id
                  )
                : null;
              const level1 = (level0?.children || []).find(
                (s: any) => (s.slug || String(s.id ?? "")) === id
              );
              if (level1) {
                setCurrentCategoryId(level1.id);
                if (level1.children && level1.children.length > 0) {
                  setSubcategories(level1.children || []);
                } else {
                  // No children, navigate to product list page
                  navigate(`/products/category/${level1.id}`);
                }
              }
            }}
          />
        </div>

        {/* Sidebar overlay for mobile */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-40 flex lg:hidden">
            <div
              className="fixed inset-0 bg-black/40"
              onClick={() => setSidebarOpen(false)}
            />
            <div className="relative w-72 bg-white shadow-xl">
              <CategorySidebar
                className="h-full overflow-y-auto"
                categories={topCategories}
                selectedLevel0Id={selectedLevel0Id}
                selectedLevel1Id={selectedLevel1Id}
                onSelectLevel0={(id) => {
                  // Handle deselection (empty string)
                  if (!id || id === "") {
                    setSelectedLevel0Id(null);
                    setSelectedLevel1Id(null);
                    setSubcategories([]);
                    setShowProducts(false);
                    setProducts([]);
                    return;
                  }
                  setSelectedLevel0Id(id);
                  setSelectedLevel1Id(null);
                  const level0 = topCategories.find(
                    (c) => (c.slug || String(c.id ?? "")) === id
                  );
                  if (level0) {
                    setCurrentCategoryId(level0.id);
                    if (level0.children && level0.children.length > 0) {
                      setSubcategories(level0.children);
                    } else {
                      // No children, navigate to product list page
                      navigate(`/products/category/${level0.id}`);
                    }
                  }
                }}
                onSelectLevel1={(id) => {
                  setSelectedLevel1Id(id);
                  const level0 = selectedLevel0Id
                    ? topCategories.find(
                        (c) =>
                          (c.slug || String(c.id ?? "")) === selectedLevel0Id
                      )
                    : null;
                  const level1 = (level0?.children || []).find(
                    (s: any) => (s.slug || String(s.id ?? "")) === id
                  );
                  if (level1) {
                    setCurrentCategoryId(level1.id);
                    if (level1.children && level1.children.length > 0) {
                      setSubcategories(level1.children || []);
                    } else {
                      // No children, navigate to product list page
                      navigate(`/products/category/${level1.id}`);
                    }
                  }
                }}
              />
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          {/* Category Header */}
          <div className=" border-b py-6 px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  {selectedLevel1Id && level0Node && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        // Go back to level 2 categories
                        setSelectedLevel1Id(null);
                        setSubcategories(level0Node?.children || []);
                      }}
                      className="text-gray-600 hover:text-orange-600"
                    >
                      ← Back to {level0Node.name}
                    </Button>
                  )}
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
                  {selectedLevel1Id && level1Node
                    ? level1Node.name
                    : getCategoryTitle(categorySlug || "")}
                </h1>
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="text-gray-500">Categories</span>
                  <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                  {level0Node ? (
                    <>
                      <span className="text-foreground font-medium">
                        {level0Node.name}
                      </span>
                      {level1Node && (
                        <>
                          <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                          <span className="text-foreground">
                            {level1Node.name}
                          </span>
                        </>
                      )}
                    </>
                  ) : (
                    <span className="text-gray-500">Browse</span>
                  )}
                </div>
                <p className="text-gray-600">
                  {filteredSubcategories.length} subcategories available
                </p>
              </div>
              <Badge className="bg-orange-100 text-sm capitalize">
                {categorySlug}
              </Badge>
            </div>
          </div>

          {/* Filters + Search */}
          <div className="bg-white border-b border-gray-200 py-4 px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
              {/* Search */}
              <div className="relative flex-1 w-full max-w-md">
                <Input
                  placeholder="Search subcategories..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-full"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              </div>

              {/* Filters + Sort + View */}
              <div className="flex items-center gap-3 w-full lg:w-auto justify-between lg:justify-end">
                {/* Mobile toggle button */}
                <Button
                  variant="outline"
                  size="sm"
                  className="lg:hidden"
                  onClick={() => setSidebarOpen(true)}
                >
                  <SlidersHorizontal className="h-4 w-4 mr-2" />
                  Categories
                </Button>

                <Button variant="outline" size="sm" className="hidden lg:flex">
                  <SlidersHorizontal className="h-4 w-4 mr-2" />
                  Filters
                </Button>

                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Sort by..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="relevance">Relevance</SelectItem>
                    <SelectItem value="products">Most Products</SelectItem>
                    <SelectItem value="newest">Newest First</SelectItem>
                  </SelectContent>
                </Select>

                <div className="flex border border-orange-300 rounded-md overflow-hidden">
                  <Button
                    variant={viewMode === "grid" ? "default" : "ghost"}
                    size="sm"
                    className={`rounded-none ${
                      viewMode === "grid"
                        ? "bg-orange-500 text-white"
                        : "text-orange-600"
                    }`}
                    onClick={() => setViewMode("grid")}
                  >
                    <Grid3X3 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === "list" ? "default" : "ghost"}
                    size="sm"
                    className={`rounded-none ${
                      viewMode === "list"
                        ? "bg-orange-500 text-white"
                        : "text-orange-600"
                    }`}
                    onClick={() => setViewMode("list")}
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Subcategories Grid */}
          <div className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="text-center py-12 text-lg">Loading...</div>
            ) : error ? (
              <div className="text-center py-12 text-red-600">{error}</div>
            ) : filteredSubcategories.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                No subcategories found.
              </div>
            ) : (
              <div
                className={`grid gap-6 ${
                  viewMode === "grid"
                    ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                    : "grid-cols-1"
                }`}
              >
                {filteredSubcategories.map((sub: any) => (
                  <Card
                    key={sub.id}
                    className="group cursor-pointer border border-gray-200 hover:shadow-lg transition-all duration-300 py-0"
                    onClick={() => handleSubcategoryClick(sub)}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={sub.coverImage || sub.banner || sub.icon || PLACEHOLDER_IMAGE}
                        alt={sub.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

                      {sub.trending && (
                        <div className="absolute top-3 left-3">
                          <Badge className="bg-orange-500 text-white flex items-center gap-1">
                            <TrendingUp className="h-3 w-3" />
                            Trending
                          </Badge>
                        </div>
                      )}

                      <div className="absolute bottom-3 left-3">
                        <Badge className="bg-white/90 text-gray-800">
                          {sub._count?.products || sub.productCount || 0}{" "}
                          Products
                        </Badge>
                      </div>
                    </div>

                    <CardContent className="p-4">
                      <h3 className="font-semibold text-gray-900 group-hover:text-orange-600 transition-colors mb-2">
                        {sub.name}
                      </h3>
                      <p className="text-sm text-gray-600">{sub.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <BizcoverIndiaFigmaFooter />
    </div>
  );
};

export default CategoryDetail;
